import React, { useEffect, useState } from "react";
import { Button, Input, Modal, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { ReactComponent as Deleted } from "../../../../assets/Icons/deleted.svg";
import trash from "../../../../assets/Images/Trash_Full.svg";
import edit from "../../../../assets/Images/Edit_Pencil_01.svg";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";
import FetchData from "../../../../hooks/FetchData";
import PostUpdateData from "../../../../hooks/postUpdate";
import CertificatePreview from "../../Addcertificate/CertificatePreview";

// Styled Component Container for all sections
const CertificatePageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .search-input {
    width: 200px !important;
    margin-right: 16px;
    border-radius: 20px;
  }

  /* Figma: fixed three-column grid, left aligned - space-between used to
     push a two-card row out to opposite edges. */
  .certificates-container {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 24px;

    @media (max-width: 1100px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 640px) {
      grid-template-columns: 1fr;
    }
  }

  .certificate-preview {
    border: 1px solid #ebebeb;
    border-radius: 10px;
  }

  .card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 12px;
    font-weight: 500;
    font-size: 16px;
    color: #1f243b;

    p {
      margin: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .button-group {
    display: flex;
    gap: 20px;
    flex-shrink: 0;

    img {
      cursor: pointer;
    }
  }

  .empty-state {
    grid-column: 1 / -1;
    color: #636980;
    padding: 40px 0;
  }
`;

// Declared outside Certificate so typing in Search doesn't remount every
// card (and re-measure its preview) on each keystroke.
const CertificateCard = ({ certificate, onEdit, onDelete }) => (
  <div>
    <CertificatePreview
      className="certificate-preview"
      backgroundImage={certificate.background_image}
      certificateType={certificate.certificate_type}
      alignment={certificate.text_alignment}
    />
    <div className="card-footer">
      <p title={certificate.certificate_name}>{certificate.certificate_name}</p>
      <div className="button-group">
        <img src={edit} alt="Edit" onClick={() => onEdit(certificate)} />
        <img src={trash} alt="Delete" onClick={() => onDelete(certificate)} />
      </div>
    </div>
  </div>
);

function Certificate() {
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.authTracking.userInfo);

  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletedModalVisible, setDeletedModalVisible] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchCertificates = async () => {
    if (!userInfo?.clientId) return;
    setLoading(true);
    try {
      const response = await FetchData({
        endPoint: `/settings/getcertificate/${userInfo.clientId}`,
      });
      setCertificates(response?.response || []);
    } catch (error) {
      console.error("Error fetching certificates:", error);
      message.error("Unable to load certificates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCertificates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.clientId]);

  const showModal = (certificate) => {
    setDeleteTarget(certificate);
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await PostUpdateData({
        endPoint: `/settings/deletecertificate/${userInfo.clientId}/${deleteTarget.certificate_id}`,
        method: "DELETE",
      });
      setIsModalOpen(false);
      setDeletedModalVisible(true);
      fetchCertificates();
      setTimeout(() => {
        setDeletedModalVisible(false);
      }, 2000);
    } catch (error) {
      console.error("Error deleting certificate:", error);
      setIsModalOpen(false);
      message.error(error?.response?.data?.message || "Failed to delete certificate");
    } finally {
      setDeleteTarget(null);
    }
  };

  const filteredCertificates = certificates.filter((c) =>
    (c.certificate_name || "").toLowerCase().includes(searchText.trim().toLowerCase())
  );

  const handleEdit = (certificate) =>
    navigate(`/editcertificate?id=${certificate.certificate_id}`, { state: { certificate } });

  return (
    <CertificatePageContainer>
      <div className="header">
        <Input
          placeholder="Search"
          prefix={<SearchOutlined style={{ color: "#636980" }} />}
          className="search-input"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Button
          className="create-field-button"
          onClick={() => navigate("/addcertificate")}
        >
          + Add Certificate
        </Button>
      </div>

      <div className="certificates-container">
        {!loading && filteredCertificates.length === 0 && (
          <p className="empty-state">No certificates created yet.</p>
        )}
        {filteredCertificates.map((certificate) => (
          <CertificateCard
            key={certificate.certificate_id}
            certificate={certificate}
            onEdit={handleEdit}
            onDelete={showModal}
          />
        ))}
      </div>

      <Modal
        width={440}
        height={177}
        title={
          <div
            style={{
              fontSize: "18px",
              fontWeight: 500,
              textAlign: "left",
            }}
          >
            Delete the Certificate
          </div>
        }
        open={isModalOpen}
        onCancel={handleCancel}
        footer={[
          <div
            style={{
              display: "flex",
              marginLeft: "100px",
              gap: "20px",
            }}
          >
            <Button
              key="cancel"
              onClick={handleCancel}
              style={{
                width: "130px",
                height: "34px",
                padding: "6.5px 38.5px 6.5px 39.5px",
                borderRadius: "8px",
                border: "1px solid #1F243B",
                fontWeight: "500",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: 400,
                }}
              >
                Cancel
              </div>
            </Button>

            <Button
              key="delete"
              onClick={handleDelete}
              style={{
                width: "130px",
                height: "34px",
                padding: "6.5px 40.5px 6.5px 41.5px",
                borderRadius: "8px",
                border: "1px solid #C2FAE5",
                backgroundColor: "#C2FAE5",
              }}
            >
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: 400,
                }}
              >
                Delete
              </div>
            </Button>
          </div>,
        ]}
        centered
        closable={false}
      >
        <p
          style={{
            fontSize: "16px",
            fontWeight: 400,
            textAlign: "left",
            color: "#636980",
          }}
        >
          Are you sure you want to delete this certificate?
        </p>
        <br />
      </Modal>
      <Modal
        open={deletedModalVisible}
        footer={null}
        centered
        closable={false}
        width={198}
        height={100}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            fontSize: "16px",
            fontWeight: "500",
            textAlign: "center",
          }}
        >
          <Deleted />
          <p
            style={{
              fontSize: "16px",
              fontWeight: 400,
            }}
          >
            Deleted
          </p>
        </div>
      </Modal>
    </CertificatePageContainer>
  );
}

export default Certificate;
